// A building's landlord consent pack history: the stages the Amazon overview dates, the
// consent signed among them, then every email Legal logged about the pack, latest first.
import { useState } from "react";

import type {
  LandlordPackHistory,
  LandlordPackStage,
} from "../api/hooks/useProjectAgentStatus";
import { formatDay } from "../utils/attributeFormat";
import { Markdown } from "./Markdown";
import { SectionBlock, SectionCard } from "./SpecialistRowSections";
import type { Citations } from "./useCitations";
import styles from "./EmailHistory.module.css";

const STAGES: { key: LandlordPackStage; label: string }[] = [
  { key: "to_amazon", label: "Sent to Amazon" },
  { key: "to_landlord", label: "Sent to landlord's side" },
  { key: "feedback", label: "First landlord feedback" },
  { key: "signed", label: "Consent signed" },
];

export const LandlordPackCard = ({
  history,
  citations,
}: {
  history: LandlordPackHistory;
  /** The tab's one lookup over every body it shows, entries' emails included. */
  citations: Citations;
}) => {
  const [showWithdrawn, setShowWithdrawn] = useState(false);
  const withdrawn = history.entries.filter((e) => e.withdrawn_reason !== null);
  const shown = showWithdrawn
    ? history.entries
    : history.entries.filter((e) => e.withdrawn_reason === null);
  const email = (text: string) => (
    <Markdown components={citations.components} inline>
      {citations.transform(text)}
    </Markdown>
  );
  return (
    <SectionCard
      title="Landlord consent pack"
      id="landlord-pack"
      aside={
        withdrawn.length ? (
          <button
            type="button"
            className={styles.toggle}
            onClick={() => setShowWithdrawn((v) => !v)}
          >
            {showWithdrawn ? "Hide" : "Show"} {withdrawn.length} withdrawn
          </button>
        ) : null
      }
    >
      <div className={styles.stages}>
        {STAGES.map(({ key, label }) => {
          const stage = history.stages[key];
          return (
            <div key={key} className={styles.stage}>
              <div className={styles.stageLabel}>{label}</div>
              {stage ? (
                <>
                  <div className={styles.stageDate}>
                    {formatDay(stage.date)}
                  </div>
                  <div className={styles.stageWho}>
                    {stage.sender}, {email(stage.email)}
                  </div>
                </>
              ) : (
                <div className={styles.notRecorded}>Not recorded</div>
              )}
            </div>
          );
        })}
      </div>
      {shown.length === 0 ? (
        <SectionBlock>
          <p className={styles.empty}>
            {withdrawn.length
              ? "Every entry Legal logged has been withdrawn."
              : "Legal has not logged any email about this building's pack yet."}
          </p>
        </SectionBlock>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Date</th>
                <th>What</th>
                <th>From</th>
                <th>To</th>
                <th>Email</th>
                <th>What it shows</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((e) => (
                <tr
                  key={e.entry_id}
                  className={
                    e.withdrawn_reason !== null ? styles.withdrawn : undefined
                  }
                >
                  <td
                    className={`${styles.date} ${e.dates_stage ? styles.first : ""}`}
                  >
                    {formatDay(e.date)}
                  </td>
                  <td>
                    <span className={`${styles.tag} ${styles[e.tone]}`}>
                      {e.kind_label}
                    </span>
                    {e.dates_stage && (
                      <span className={styles.note}>Dates the stage</span>
                    )}
                    {e.referenced && (
                      <span className={styles.note}>
                        Refers to the {e.kind === "followed_up" ? "follow-up" : "send"} rather
                        than being it
                      </span>
                    )}
                  </td>
                  <td>{e.sender}</td>
                  <td>{e.recipients.join("; ")}</td>
                  <td>{email(e.email)}</td>
                  <td className={styles.summary}>
                    {e.summary}
                    {e.points.length > 0 && (
                      <ul className={styles.points}>
                        {e.points.map((point, i) => (
                          <li key={i}>{point}</li>
                        ))}
                      </ul>
                    )}
                    {e.withdrawn_reason !== null && (
                      <span className={styles.note}>
                        Withdrawn: {e.withdrawn_reason}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
};
